/**
 * Окно выбора элементов для публикации (14.10.26, №259, Task 7): вкладки «Фото»/«Видео», мульти-выбор.
 *
 * Режим `create` (кнопка «Новая публикация» на `/posts`): клик по плитке — как раньше, сразу создаёт
 * публикацию (спросит, если у фото уже есть черновик); отдельная галочка в углу плитки набирает
 * несколько элементов для кнопки «Создать публикацию (N)» (порядок отметок = порядок карусели, первый —
 * обложка). Режим `add` (кнопка «Добавить» в полосе «Что публикуем» на `/posts/:id`): клик по плитке
 * ТОЛЬКО отмечает (как в остальных окнах выбора — `RefPickerDialog`), кнопка «Добавить (N)» отдаёт
 * отмеченное наверх — саму мутацию (`addMedia`) и дорожку busy/STALE ведёт `PostPlanPage`, окно их не знает.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useEffect, useState } from "react";

import { Dialog } from "@/components/trends/Dialog";
import { RetryImg } from "@/components/trends/PinGrid";
import { btn, btnPrimary, Empty, ErrorLine, Loading, Segmented } from "@/components/trends/ui";
import { thumbUrl } from "@/lib/media";
import { videoCaption } from "@/lib/postMedia";
import type { PostCandidate } from "@/lib/postPlan";
import { createPlan, createPlanFromMany, loadCandidates, postsKeys } from "@/lib/postsApi";

const STATUS_WORD: Record<string, string> = { draft: "черновик", approved: "утверждена", cancelled: "отменена" };

type Kind = "photo" | "video";
export type PickerMode = "create" | "add";

export function CandidatePicker({
  open,
  mode = "create",
  onClose,
  onCreated,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  /** `create` (умолчание) — «Публикации», одиночный клик создаёт сразу; `add` — полоса «Что публикуем»
   * существующего черновика, клик только отмечает. */
  mode?: PickerMode;
  /** Режим `create`: публикация создана (одиночным кликом или «Создать публикацию (N)»). */
  onCreated?: (planId: number) => void;
  /** Режим `add`: отмеченные варианты — мутацию и закрытие окна ведёт вызывающий. */
  onAdd?: (variantIds: number[]) => void;
}) {
  const [tab, setTab] = useState<Kind>("photo");
  const [picked, setPicked] = useState<number[]>([]);
  const [asking, setAsking] = useState<PostCandidate | null>(null);

  // каждое открытие — чистое состояние: вкладка, отметки и вопрос «уже есть публикация» не переживают закрытие
  useEffect(() => {
    if (open) {
      setTab("photo");
      setPicked([]);
      setAsking(null);
    }
  }, [open]);

  const list = useQuery({
    queryKey: postsKeys.candidates(undefined, tab),
    queryFn: () => loadCandidates(undefined, tab),
    enabled: open,
  });
  const create = useMutation({
    mutationFn: (variantId: number) => createPlan(variantId),
    onSuccess: (plan) => onCreated?.(plan.id),
  });
  const createMany = useMutation({
    mutationFn: (variantIds: number[]) => createPlanFromMany(variantIds),
    onSuccess: (plan) => onCreated?.(plan.id),
  });
  const busy = create.isPending || createMany.isPending;

  const toggle = (variantId: number) =>
    setPicked((prev) => (prev.includes(variantId) ? prev.filter((id) => id !== variantId) : [...prev, variantId]));

  /** Одиночный клик в режиме «создать»: как раньше — спросит про существующий черновик. */
  const choose = (c: PostCandidate) => {
    if (c.plans.length > 0) setAsking(c);
    else create.mutate(c.variant_id);
  };
  const draft = asking?.plans.find((p) => p.status === "draft");

  const title = mode === "add" ? "Добавить элементы в публикацию" : "Новая публикация: выберите фото или видео";

  return (
    <Dialog open={open} onClose={onClose} title={title} wide>
      <div className="flex flex-col gap-3 p-4">
        <Segmented<Kind>
          label="вид"
          value={tab}
          onPick={(v) => {
            setTab(v);
            setAsking(null);
          }}
          options={[
            { value: "photo", label: "Фото" },
            { value: "video", label: "Видео" },
          ]}
        />
        {asking ? (
          <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]">
            <span className="text-fg">У этого фото уже есть публикация.</span>
            {draft ? (
              <button type="button" className={btn} onClick={() => onCreated?.(draft.id)}>
                Открыть черновик {draft.id}
              </button>
            ) : null}
            {asking.plans
              .filter((p) => p.status !== "draft")
              .map((p) => (
                <button key={p.id} type="button" className={btn} onClick={() => onCreated?.(p.id)}>
                  Открыть {p.id} ({STATUS_WORD[p.status] ?? p.status})
                </button>
              ))}
            <button type="button" className={btn} disabled={busy} onClick={() => create.mutate(asking.variant_id)}>
              Создать новую
            </button>
            <button type="button" className={btn} onClick={() => setAsking(null)}>
              Назад
            </button>
          </div>
        ) : null}
        {create.isError ? <ErrorLine error={create.error} /> : null}
        {createMany.isError ? <ErrorLine error={createMany.error} /> : null}
        {list.isPending ? <Loading /> : null}
        {list.isError ? <ErrorLine error={list.error} /> : null}
        {list.data && list.data.length === 0 ? (
          <Empty>
            {tab === "video"
              ? "Готовых видео нет — клип проекта, загруженное видео или смонтированный ролик."
              : "Нет фото с меткой «В публикацию». Отметьте улучшенное или загруженное фото в «Результатах»."}
          </Empty>
        ) : null}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
          {(list.data ?? []).map((c) => (
            <Tile
              key={c.variant_id}
              c={c}
              isVideo={tab === "video"}
              mode={mode}
              selected={picked.includes(c.variant_id)}
              disabled={busy}
              onToggle={() => toggle(c.variant_id)}
              onChoose={() => choose(c)}
            />
          ))}
        </div>
      </div>
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-bg px-4 py-2.5">
        <span className="mr-auto text-[12px] text-fg-faint tnum">
          {picked.length > 0
            ? `отмечено: ${picked.length}`
            : mode === "add"
              ? "клик по плитке отмечает"
              : "клик по плитке — сразу; галочка в углу — несколько элементов"}
        </span>
        <button type="button" className={btn} onClick={onClose}>
          Отмена
        </button>
        <button
          type="button"
          className={btnPrimary}
          disabled={picked.length === 0 || busy}
          onClick={() => {
            if (mode === "add") onAdd?.(picked);
            else createMany.mutate(picked);
          }}
        >
          {mode === "add" ? `Добавить ${picked.length || ""}` : `Создать публикацию ${picked.length || ""}`}
        </button>
      </div>
    </Dialog>
  );
}

function Tile({
  c,
  isVideo,
  mode,
  selected,
  disabled,
  onToggle,
  onChoose,
}: {
  c: PostCandidate;
  isVideo: boolean;
  mode: PickerMode;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
  onChoose: () => void;
}) {
  const src = isVideo ? (c.poster_path ?? null) : c.asset_path;
  const body = (
    <>
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-[var(--radius-ui)] bg-bg-subtle">
        {src ? (
          <RetryImg src={thumbUrl(src)} alt="" className="size-full object-cover" />
        ) : (
          <div className="grid size-full place-items-center p-1 text-center text-[11px] text-fg-faint">нет постера</div>
        )}
        {isVideo ? (
          <span className="absolute bottom-1 right-1 rounded-[var(--radius-ui)] bg-black/60 px-1 text-[10px] text-white">
            {videoCaption(c.video_ms)}
          </span>
        ) : null}
      </div>
      <span className="truncate text-[12px] text-fg">{c.series ? `серия ${c.series}` : "нет серии CRM"}</span>
      {c.plans.length > 0 ? (
        <span className="truncate text-[11px] text-fg-faint">есть публикация {c.plans.map((p) => p.id).join(", ")}</span>
      ) : null}
    </>
  );

  if (mode === "add") {
    // как у остальных окон выбора (`RefPickerDialog`): клик по плитке переключает отметку
    return (
      <button
        type="button"
        disabled={disabled}
        aria-pressed={selected}
        onClick={onToggle}
        className={`flex flex-col gap-1 rounded-[var(--radius-card)] border bg-bg p-1.5 text-left transition-colors disabled:opacity-50 ${
          selected ? "border-accent" : "border-border hover:border-accent"
        }`}
      >
        {body}
      </button>
    );
  }

  // режим «создать»: клик по плитке сразу создаёт (как раньше); отдельная галочка в углу — набор для «Создать публикацию (N)»
  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={onChoose}
        className="flex w-full flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg p-1.5 text-left transition-colors hover:border-accent disabled:opacity-50"
      >
        {body}
      </button>
      <button
        type="button"
        aria-pressed={selected}
        aria-label={selected ? "Снять отметку" : "Отметить для нескольких элементов"}
        title="Отметить для публикации из нескольких элементов"
        onClick={onToggle}
        className={`absolute top-2 right-2 grid size-5 place-items-center rounded-[4px] border ${
          selected ? "border-accent bg-accent text-bg" : "border-white/80 bg-black/45 text-white hover:bg-black/65"
        }`}
      >
        {selected ? <Check size={13} strokeWidth={3} /> : null}
      </button>
    </div>
  );
}
